// Chemin: C:\gestion-copro\src\services\pdfMandat.service.ts
// Génération PDF du mandat de coordination signé
// Session 90 : Création initiale
// Session 91 : Support UTF-8 (accents français) via police Liberation Sans
//              + Tableau signatures avec noms des signataires attendus (croisement membresCS)
//              + En-tête professionnelle améliorée
// Référence : SPECIFICATION_GOUVERNANCE_V2.md §5
// IMPORTANT : Pas d'emojis dans jsPDF (bug Unicode)

import jsPDF from 'jspdf';
import { MandatCoordination, SignatureMandat } from '../types/mandat.types';
import { LIBERATION_SANS_REGULAR } from '../assets/fonts/LiberationSans-Regular';
import { LIBERATION_SANS_BOLD } from '../assets/fonts/LiberationSans-Bold';

// ============================================================================
// CONFIGURATION
// ============================================================================

const CONFIG = {
  marginLeft: 25,
  marginRight: 25,
  marginTop: 20,
  marginBottom: 25,
  pageWidth: 210,
  pageHeight: 297,
  fontSizeNormal: 10,
  fontSizeSmall: 8,
  fontSizeMedium: 11,
  fontSizeLarge: 14,
  fontSizeTitle: 16,
  lineHeight: 5.5,
  paragraphSpacing: 8,
};

// Couleurs Copro Protect
const COLORS = {
  primary: [30, 64, 175] as [number, number, number],
  primaryLight: [219, 234, 254] as [number, number, number],
  dark: [0, 0, 0] as [number, number, number],
  gray: [100, 100, 100] as [number, number, number],
  lightGray: [200, 200, 200] as [number, number, number],
  footerGray: [150, 150, 150] as [number, number, number],
  success: [0, 128, 0] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  tableHeader: [240, 244, 255] as [number, number, number],
  tableAlt: [248, 250, 252] as [number, number, number],
  orange: [234, 88, 12] as [number, number, number],
};

// ============================================================================
// INITIALISATION POLICES UTF-8
// ============================================================================

const initFonts = (doc: jsPDF): void => {
  doc.addFileToVFS('LiberationSans-Regular.ttf', LIBERATION_SANS_REGULAR);
  doc.addFont('LiberationSans-Regular.ttf', 'LiberationSans', 'normal');

  doc.addFileToVFS('LiberationSans-Bold.ttf', LIBERATION_SANS_BOLD);
  doc.addFont('LiberationSans-Bold.ttf', 'LiberationSans', 'bold');

  doc.setFont('LiberationSans', 'normal');
};

const setFont = (doc: jsPDF, style: 'normal' | 'bold') => {
  doc.setFont('LiberationSans', style);
};

// ============================================================================
// UTILITAIRES
// ============================================================================

const cleanTextForPDF = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '')
    .replace(/[\u{2600}-\u{26FF}]/gu, '')
    .replace(/[\u{2700}-\u{27BF}]/gu, '')
    .replace(/["\u201C\u201D]/g, '"')
    .replace(/['\u2018\u2019]/g, "'")
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
};

const formatDateFr = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatDateHeureFr = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  }) + ' à ' + date.toLocaleTimeString('fr-FR', {
    hour: '2-digit', minute: '2-digit'
  });
};

const splitTextToLines = (doc: jsPDF, text: string, maxWidth: number): string[] => {
  const cleanText = cleanTextForPDF(text);
  return doc.splitTextToSize(cleanText, maxWidth);
};

// ============================================================================
// VÉRIFICATION SAUT DE PAGE
// ============================================================================

const checkPageBreak = (doc: jsPDF, yPos: number, requiredSpace: number): number => {
  if (yPos + requiredSpace > CONFIG.pageHeight - CONFIG.marginBottom) {
    doc.addPage();
    return CONFIG.marginTop;
  }
  return yPos;
};

// ============================================================================
// TYPES POUR LE PDF
// ============================================================================

/**
 * Données d'un signataire pour le tableau PDF
 * Permet d'afficher les noms même si le membre n'a pas encore signé
 */
export interface SignatairePDF {
  membreCSId: string;
  nom: string;            // "Prénom NOM"
  dateSignature?: any;    // Timestamp ou null
  ip?: string;
}

export interface PDFMandatOptions {
  mandat: MandatCoordination;
  adresseCopropriete?: string;
  /** Liste des signataires attendus (croisement membresCS + signatures) */
  signataires?: SignatairePDF[];
}

// ============================================================================
// GÉNÉRATION PDF
// ============================================================================

export const genererPDFMandat = (options: PDFMandatOptions): Blob => {
  const { mandat, adresseCopropriete, signataires } = options;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const contentWidth = CONFIG.pageWidth - CONFIG.marginLeft - CONFIG.marginRight;
  let yPos = CONFIG.marginTop;

  // Initialiser les polices UTF-8
  initFonts(doc);

  // ========================================
  // EN-TÊTE
  // ========================================

  // Bandeau bleu supérieur
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, CONFIG.pageWidth, 4, 'F');
  yPos = 14;

  // Titre principal
  doc.setFontSize(CONFIG.fontSizeTitle);
  setFont(doc, 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('MANDAT DE COORDINATION', CONFIG.pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;

  // Sous-titre
  doc.setFontSize(CONFIG.fontSizeMedium);
  setFont(doc, 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text('Conseil Syndical — Plateforme Copro Protect', CONFIG.pageWidth / 2, yPos, { align: 'center' });
  yPos += 6;

  // Filet sous en-tête
  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.3);
  doc.line(CONFIG.marginLeft, yPos, CONFIG.pageWidth - CONFIG.marginRight, yPos);
  yPos += CONFIG.paragraphSpacing;

  // ========================================
  // INFORMATIONS COPROPRIÉTÉ
  // ========================================

  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(CONFIG.fontSizeNormal);

  const labelX = CONFIG.marginLeft;
  const valueX = CONFIG.marginLeft + 45;

  setFont(doc, 'bold');
  doc.text('Copropriété :', labelX, yPos);
  setFont(doc, 'normal');
  doc.text(cleanTextForPDF(mandat.nomCopropriete), valueX, yPos);
  yPos += CONFIG.lineHeight;

  if (adresseCopropriete) {
    setFont(doc, 'bold');
    doc.text('Adresse :', labelX, yPos);
    setFont(doc, 'normal');
    doc.text(cleanTextForPDF(adresseCopropriete), valueX, yPos);
    yPos += CONFIG.lineHeight;
  }

  if (mandat.dateAG) {
    setFont(doc, 'bold');
    doc.text('AG du :', labelX, yPos);
    setFont(doc, 'normal');
    doc.text(formatDateFr(mandat.dateAG), valueX, yPos);
    yPos += CONFIG.lineHeight;
  }

  setFont(doc, 'bold');
  doc.text('Date du mandat :', labelX, yPos);
  setFont(doc, 'normal');
  doc.text(formatDateFr(mandat.dateCreation), valueX, yPos);
  yPos += CONFIG.lineHeight;

  setFont(doc, 'bold');
  doc.text('Coordinateur désigné :', labelX, yPos);
  setFont(doc, 'normal');
  doc.text(cleanTextForPDF(mandat.coordinateurNom), valueX, yPos);
  yPos += CONFIG.lineHeight;

  setFont(doc, 'bold');
  doc.text('Statut :', labelX, yPos);
  setFont(doc, 'normal');
  doc.text(mandat.estMembreCS ? 'Membre élu du Conseil Syndical' : 'Personne externe mandatée', valueX, yPos);
  yPos += CONFIG.paragraphSpacing + 4;

  // ========================================
  // TEXTE DU MANDAT
  // ========================================

  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(CONFIG.marginLeft, yPos, CONFIG.marginLeft + 50, yPos);
  yPos += 6;

  doc.setFontSize(CONFIG.fontSizeLarge);
  setFont(doc, 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Texte du mandat', CONFIG.marginLeft, yPos);
  yPos += CONFIG.paragraphSpacing;

  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(CONFIG.fontSizeNormal);
  setFont(doc, 'normal');

  const dateAGStr = mandat.dateAG ? formatDateFr(mandat.dateAG) : undefined;

  const texteComplet = genererTexteContenu(
    mandat.nomCopropriete,
    mandat.coordinateurNom,
    dateAGStr
  );

  const sections = texteComplet.split('\n\n');

  for (const section of sections) {
    const trimmed = section.trim();
    if (!trimmed) continue;

    yPos = checkPageBreak(doc, yPos, 15);

    if (trimmed.length < 60 && !trimmed.endsWith('.') && !trimmed.endsWith(':') && !trimmed.startsWith('-')) {
      setFont(doc, 'bold');
      doc.setFontSize(CONFIG.fontSizeMedium);
      const lines = splitTextToLines(doc, trimmed, contentWidth);
      lines.forEach(line => {
        yPos = checkPageBreak(doc, yPos, CONFIG.lineHeight);
        doc.text(line, CONFIG.marginLeft, yPos);
        yPos += CONFIG.lineHeight;
      });
      setFont(doc, 'normal');
      doc.setFontSize(CONFIG.fontSizeNormal);
      yPos += 2;
    } else {
      const lignes = trimmed.split('\n');
      for (const ligne of lignes) {
        const l = ligne.trim();
        if (!l) continue;

        yPos = checkPageBreak(doc, yPos, CONFIG.lineHeight);

        if (l.startsWith('- ')) {
          const bulletText = l.substring(2);
          const bulletLines = splitTextToLines(doc, bulletText, contentWidth - 8);
          bulletLines.forEach((bl, idx) => {
            yPos = checkPageBreak(doc, yPos, CONFIG.lineHeight);
            if (idx === 0) {
              doc.text('\u2022', CONFIG.marginLeft + 3, yPos);
            }
            doc.text(bl, CONFIG.marginLeft + 8, yPos);
            yPos += CONFIG.lineHeight;
          });
        } else {
          const paraLines = splitTextToLines(doc, l, contentWidth);
          paraLines.forEach(pl => {
            yPos = checkPageBreak(doc, yPos, CONFIG.lineHeight);
            doc.text(pl, CONFIG.marginLeft, yPos);
            yPos += CONFIG.lineHeight;
          });
        }
      }
      yPos += 3;
    }
  }

  // ========================================
  // TABLEAU DES SIGNATURES
  // ========================================

  yPos += CONFIG.paragraphSpacing;
  yPos = checkPageBreak(doc, yPos, 60);

  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(CONFIG.marginLeft, yPos, CONFIG.marginLeft + 50, yPos);
  yPos += 6;

  doc.setFontSize(CONFIG.fontSizeLarge);
  setFont(doc, 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Signatures des membres du Conseil Syndical', CONFIG.marginLeft, yPos);
  yPos += CONFIG.paragraphSpacing;

  doc.setTextColor(...COLORS.dark);

  const colNom = CONFIG.marginLeft;
  const colStatut = CONFIG.marginLeft + 55;
  const colDate = CONFIG.marginLeft + 90;
  const colIP = CONFIG.marginLeft + 130;
  const tableRight = CONFIG.pageWidth - CONFIG.marginRight;

  // Fond en-tête
  doc.setFillColor(...COLORS.tableHeader);
  doc.rect(CONFIG.marginLeft, yPos - 4, contentWidth, 7, 'F');

  doc.setFontSize(CONFIG.fontSizeSmall);
  setFont(doc, 'bold');
  doc.text('Membre', colNom + 2, yPos);
  doc.text('Statut', colStatut + 2, yPos);
  doc.text('Date de signature', colDate + 2, yPos);
  doc.text('Adresse IP', colIP + 2, yPos);
  yPos += 7;

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(CONFIG.marginLeft, yPos - 3, tableRight, yPos - 3);

  // Utiliser les signataires fournis, sinon fallback sur mandat.signatures
  const lignesSignatures: SignatairePDF[] = signataires && signataires.length > 0
    ? signataires
    : Object.entries(mandat.signatures || {}).map(([id, sig]) => ({
        membreCSId: id,
        nom: (sig as SignatureMandat).nom || 'Non renseigné',
        dateSignature: (sig as SignatureMandat).dateSignature,
        ip: (sig as SignatureMandat).ip,
      }));

  // Trier : signés en premier (par date), puis en attente (alphabétique)
  lignesSignatures.sort((a, b) => {
    const aSignee = a.dateSignature ? 1 : 0;
    const bSignee = b.dateSignature ? 1 : 0;
    if (aSignee !== bSignee) return bSignee - aSignee;
    if (a.dateSignature && b.dateSignature) {
      const dateA = a.dateSignature?.toDate ? a.dateSignature.toDate().getTime() : new Date(a.dateSignature).getTime();
      const dateB = b.dateSignature?.toDate ? b.dateSignature.toDate().getTime() : new Date(b.dateSignature).getTime();
      return dateA - dateB;
    }
    return (a.nom || '').localeCompare(b.nom || '');
  });

  setFont(doc, 'normal');
  doc.setFontSize(CONFIG.fontSizeNormal);

  const signaturesEffectives = lignesSignatures.filter(s => s.dateSignature);

  lignesSignatures.forEach((sig, index) => {
    yPos = checkPageBreak(doc, yPos, 8);

    if (index % 2 === 0) {
      doc.setFillColor(...COLORS.tableAlt);
      doc.rect(CONFIG.marginLeft, yPos - 4, contentWidth, 7, 'F');
    }

    setFont(doc, 'normal');
    doc.setFontSize(CONFIG.fontSizeNormal);
    doc.setTextColor(...COLORS.dark);
    doc.text(cleanTextForPDF(sig.nom || 'Non renseigné'), colNom + 2, yPos);

    if (sig.dateSignature) {
      doc.setTextColor(...COLORS.success);
      doc.setFontSize(CONFIG.fontSizeSmall);
      doc.text('Signé', colStatut + 2, yPos);
    } else {
      doc.setTextColor(...COLORS.orange);
      doc.setFontSize(CONFIG.fontSizeSmall);
      doc.text('En attente', colStatut + 2, yPos);
    }

    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(CONFIG.fontSizeSmall);
    doc.text(sig.dateSignature ? formatDateHeureFr(sig.dateSignature) : '—', colDate + 2, yPos);
    doc.text(sig.ip || '—', colIP + 2, yPos);

    doc.setFontSize(CONFIG.fontSizeNormal);
    yPos += 7;
  });

  if (lignesSignatures.length === 0) {
    yPos = checkPageBreak(doc, yPos, 8);
    doc.setFillColor(...COLORS.tableAlt);
    doc.rect(CONFIG.marginLeft, yPos - 4, contentWidth, 7, 'F');
    setFont(doc, 'normal');
    doc.setTextColor(...COLORS.gray);
    doc.text('Aucun signataire enregistré', colNom + 2, yPos);
    doc.setTextColor(...COLORS.dark);
    yPos += 7;
  }

  doc.setDrawColor(180, 180, 180);
  doc.line(CONFIG.marginLeft, yPos - 3, tableRight, yPos - 3);

  yPos += 6;
  doc.setFontSize(CONFIG.fontSizeNormal);
  setFont(doc, 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text(
    `${signaturesEffectives.length} signature(s) sur ${mandat.signaturesAttendues} attendue(s)`,
    CONFIG.marginLeft, yPos
  );

  if (mandat.dateActivation) {
    yPos += CONFIG.lineHeight;
    setFont(doc, 'normal');
    doc.setTextColor(...COLORS.success);
    doc.text(`Mandat activé le ${formatDateFr(mandat.dateActivation)}`, CONFIG.marginLeft, yPos);
    doc.setTextColor(...COLORS.dark);
  }

  // ========================================
  // PIED DE PAGE (toutes les pages)
  // ========================================

  const totalPages = (doc as any).getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    (doc as any).setPage(i);

    doc.setDrawColor(...COLORS.lightGray);
    doc.setLineWidth(0.3);
    doc.line(CONFIG.marginLeft, CONFIG.pageHeight - 15, CONFIG.pageWidth - CONFIG.marginRight, CONFIG.pageHeight - 15);

    doc.setFontSize(7);
    setFont(doc, 'normal');
    doc.setTextColor(...COLORS.footerGray);
    doc.text(
      `Mandat de coordination — ${cleanTextForPDF(mandat.nomCopropriete)} — Généré via Copro Protect`,
      CONFIG.marginLeft,
      CONFIG.pageHeight - 10
    );
    doc.text(
      `Page ${i}/${totalPages}`,
      CONFIG.pageWidth - CONFIG.marginRight,
      CONFIG.pageHeight - 10,
      { align: 'right' }
    );
  }

  return doc.output('blob');
};

// ============================================================================
// TEXTE DU MANDAT (avec accents - police Liberation Sans UTF-8)
// ============================================================================

function genererTexteContenu(
  nomCopropriete: string,
  coordinateurNom: string,
  dateAG?: string
): string {
  const dateAGTexte = dateAG ? ` du ${dateAG}` : '';

  return `Les membres soussignés du Conseil Syndical de la copropriété ${cleanTextForPDF(nomCopropriete)}, désignés par l'Assemblée Générale${dateAGTexte}, mandatent :

${cleanTextForPDF(coordinateurNom)}

en qualité de Coordinateur/Coordinatrice pour agir au nom du Conseil Syndical dans le cadre de la gestion courante via la plateforme Copro Protect.

Périmètre du mandat

Ce mandat couvre notamment :
- La rédaction et l'envoi de correspondances courantes (demandes d'information, relances, réclamations)
- L'archivage et la classification des documents dans la GED
- Le suivi des signalements et la gestion des affaires
- La gestion des interlocuteurs (syndic, promoteur, prestataires)
- L'utilisation de l'assistant SENTINELLE pour l'analyse et la préparation des communications
- La communication avec le syndic, les prestataires et les copropriétaires

Exclusions du mandat

Sont exclus de ce mandat et soumis à validation préalable d'au moins un membre du Conseil Syndical :
- Les mises en demeure formelles
- Les courriers précontentieux et contentieux
- Toute communication impliquant une menace de procédure judiciaire
- Tout engagement financier au nom du Conseil Syndical

Droit de regard

Chaque membre du Conseil Syndical conserve un droit de consultation permanent sur l'ensemble des actions réalisées via la plateforme Copro Protect. L'historique complet des communications, documents et décisions est accessible à tout moment.

Droit d'alerte

Tout membre du Conseil Syndical peut signaler a posteriori une communication qu'il estime inappropriée via la fonction « Signaler un problème » de la plateforme. Ce signalement est tracé et notifié à l'ensemble du Conseil.

Durée

Ce mandat est valable pour la durée du mandat du Conseil Syndical en cours, sauf révocation anticipée par vote majoritaire des membres du CS.`;
}
